// 組織「存取權」合併清單（SPEC #vms-web-787 IAM-10、#vms-web-909 GET /v1/orgs/{org}/access；VMS UX 評審 2026-10-13 P0-9）。
// 一列 = 誰（人 / 成員群組 / 另一個組織）在哪裡（節點 / 家戶 / 設備，完整路徑）能做什麼、從哪個來源來。
// 撤銷不走這支：照來源打各自原本的 API（can_revoke 只是提示，伺服器會再驗一次），見 features/people/AccessList.tsx。
import { kinioFetch, planeBase } from '@kinio/web-sdk';
import type { OrgRef } from './planeApi';
import { dateStamp } from './timefmt';

export type AccessSource = 'org_role' | 'home_role' | 'site_grant' | 'node_assignment' | 'share' | 'delegation';
/** 排序同伺服器：組織角色 → 家戶角色 → 場域授權 → 節點指派 → 分享 → 委派。 */
export const ACCESS_SOURCES: AccessSource[] = ['org_role', 'home_role', 'site_grant', 'node_assignment', 'share', 'delegation'];

export interface AccessRow {
  principal: { kind: 'user' | 'member_group' | 'org'; id: string; display_name?: string; email?: string };
  scope: { kind: 'node' | 'home' | 'device'; id: string; path: string; home_id?: string };
  /** 組織角色 / 家戶角色 / 範本 id / 節點角色 / 分享用途；委派沒有 */
  role?: string;
  actions: string[];
  source: AccessSource;
  /** grant_id / assignment_id / share_id / delegation_id / home_id / user_id */
  source_ref: string;
  /** 從祖先節點繼承下來的（唯讀） */
  inherited_from?: { node_id: string; path: string };
  expires_at?: string;
  schedule?: { tz?: string; windows?: { days?: number[]; from: string; to: string }[] } | null;
  can_revoke: boolean;
  /** 家戶 id（後端有帶才有；分享撤銷要用，沒有就另外找） */
  home_id?: string;
}

export interface AccessQuery {
  principal?: string;
  node?: string;
  home?: string;
  source?: AccessSource | '';
  cursor?: string;
  limit?: number;
}

export const accessApi = {
  list: (o: OrgRef, q: AccessQuery = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '' && v !== null) s.set(k, String(v));
    // 回應封套是 {accesses}（ENG-10：`data` 只留給 VMS façade 與事件搜尋；platform#89）
    return kinioFetch<{ accesses: AccessRow[]; next_cursor?: string }>(`${planeBase(o.api)}/v1/orgs/${encodeURIComponent(o.org_id)}/access${s.size ? '?' + s : ''}`).then((r) => ({
      data: r?.accesses ?? [],
      next_cursor: r?.next_cursor,
    }));
  },
};

type T = (k: string, o?: Record<string, unknown>) => string;

/** 對象顯示名：人 = 名字（沒有名字用 email），查不到 =「已離開的成員」；群組前面加 ▦。 */
export function principalName(t: T, p: AccessRow['principal']): string {
  if (p.kind === 'user') return p.display_name || p.email || t('iam.left_member');
  if (p.kind === 'member_group') return '▦ ' + (p.display_name || t('iam.a_group'));
  return p.display_name || t('iam.an_org');
}

/** 「能做什麼」：角色譯名（照來源）＋ 另勾的動作。 */
export function rowWhat(t: T, r: AccessRow, actionWords: (a: string[]) => string): string {
  switch (r.source) {
    case 'org_role': {
      const role = t(`people.org_role_${r.role}`, { defaultValue: t('iam.org_role_other') });
      const perms = r.role === 'owner' || r.role === 'org_admin' ? [] : r.actions.map((p) => t(`people.perm_${p.replace('.', '_')}`, { defaultValue: '' })).filter(Boolean);
      return perms.length ? role + t('common.paren', { x: perms.join(t('common.list_sep')) }) : role;
    }
    case 'home_role':
      return t('iam.home_role', { role: t(`people.hrole_${r.role}`, { defaultValue: t('iam.org_role_other') }) });
    case 'node_assignment': {
      const base = r.role && r.role !== 'custom' ? t('ot.role_' + r.role, { defaultValue: '' }) : '';
      return base || actionWords(r.actions);
    }
    case 'site_grant': {
      const base = r.role ? t(`roles.${r.role}`, { defaultValue: '' }) : '';
      return base || actionWords(r.actions);
    }
    default:
      return actionWords(r.actions);
  }
}

/** 期限欄：繼承 / 到期 / 時段 / 永久。 */
export function rowTerm(t: T, r: AccessRow, _lang: string): string {
  const parts: string[] = [];
  if (r.expires_at) parts.push(t('ot.until', { date: dateStamp(r.expires_at) }));
  for (const w of r.schedule?.windows ?? []) parts.push(t('ot.window', { from: w.from, to: w.to }));
  return parts.length ? parts.join(' · ') : t('ot.forever');
}

/** 依來源分組（照伺服器的來源順序）。 */
export function groupBySource(rows: AccessRow[]): { source: AccessSource; rows: AccessRow[] }[] {
  return ACCESS_SOURCES.map((s) => ({ source: s, rows: rows.filter((r) => r.source === s) })).filter((g) => g.rows.length > 0);
}
